import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, User, Mail, KeyRound, LogOut, Loader2, Check, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { TeacherShell } from "@/components/TeacherShell";
import { api, formatApiErrorDetail } from "@/lib/api";

function Card({ icon: Icon, title, children, testid }) {
  return (
    <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] dark:border-white/10 dark:bg-white/[0.03]" data-testid={testid}>
      <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200"><Icon size={16} className="text-slate-400" /> {title}</h2>
      {children}
    </section>
  );
}

export default function TeacherSettings() {
  const navigate = useNavigate();
  const { user, logout, refresh } = useAuth();

  const [profile, setProfile] = useState({ first_name: user?.first_name || "", last_name: user?.last_name || "", email: user?.email || "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [pw, setPw] = useState({ old_password: "", new_password: "", confirm_password: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delPw, setDelPw] = useState("");
  const [deleting, setDeleting] = useState(false);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.patch("/auth/profile", profile);
      await refresh();
      toast.success("Profil mis à jour.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingProfile(false); }
  };

  const savePw = async () => {
    if (!pw.old_password || !pw.new_password || !pw.confirm_password) { toast.error("Veuillez remplir tous les champs."); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", pw);
      setPw({ old_password: "", new_password: "", confirm_password: "" });
      toast.success("Mot de passe modifié.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingPw(false); }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/account", { data: { password: delPw } });
      toast.success("Votre compte a été supprimé.");
      await logout();
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setDeleting(false);
    }
  };

  return (
    <TeacherShell active="parametres">
      <div className="mx-auto max-w-2xl">
        <h1 className="mb-6 font-heading text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">Paramètres</h1>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col gap-5">
          <Card icon={User} title="Mon profil" testid="profile-card">
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5"><Label>Prénom</Label><Input value={profile.first_name} onChange={(e) => setProfile((s) => ({ ...s, first_name: e.target.value }))} data-testid="tsettings-first-name" /></div>
                <div className="flex flex-col gap-1.5"><Label>Nom</Label><Input value={profile.last_name} onChange={(e) => setProfile((s) => ({ ...s, last_name: e.target.value }))} data-testid="tsettings-last-name" /></div>
              </div>
              <div className="flex flex-col gap-1.5"><Label>Adresse e-mail</Label><Input type="email" value={profile.email} onChange={(e) => setProfile((s) => ({ ...s, email: e.target.value }))} data-testid="tsettings-email" /></div>
              <div className="flex justify-end"><Button onClick={saveProfile} disabled={savingProfile} className="rounded-xl" data-testid="save-profile-button">{savingProfile ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />}Enregistrer</Button></div>
            </div>
          </Card>

          <Card icon={KeyRound} title="Mot de passe" testid="tpassword-card">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5"><Label>Ancien mot de passe</Label><Input type="password" value={pw.old_password} onChange={(e) => setPw((s) => ({ ...s, old_password: e.target.value }))} data-testid="tsettings-old-password" /></div>
              <div className="flex flex-col gap-1.5"><Label>Nouveau mot de passe</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw((s) => ({ ...s, new_password: e.target.value }))} data-testid="tsettings-new-password" /></div>
              <div className="flex flex-col gap-1.5"><Label>Confirmer</Label><Input type="password" value={pw.confirm_password} onChange={(e) => setPw((s) => ({ ...s, confirm_password: e.target.value }))} data-testid="tsettings-confirm-password" /></div>
              <div className="flex justify-end"><Button onClick={savePw} disabled={savingPw} className="rounded-xl" data-testid="save-tpassword-button">{savingPw ? <Loader2 size={16} className="mr-2 animate-spin" /> : <KeyRound size={16} className="mr-2" />}Modifier</Button></div>
            </div>
          </Card>

          <button onClick={logout} className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-3.5 text-sm font-medium text-slate-700 hover:scale-[0.99] dark:border-white/10 dark:bg-white/5 dark:text-slate-200" data-testid="tsettings-logout"><LogOut size={16} /> Se déconnecter</button>

          <section className="rounded-2xl border border-rose-200 bg-rose-50/50 p-5 dark:border-rose-500/20 dark:bg-rose-500/5" data-testid="account-danger-zone">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-rose-700 dark:text-rose-400"><AlertTriangle size={16} /> Zone de danger</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">La suppression de votre compte efface définitivement toutes vos classes, élèves, passeports et données associées.</p>
            <Button onClick={() => { setDelPw(""); setDelOpen(true); }} className="mt-4 rounded-xl bg-rose-600 text-white hover:bg-rose-700" data-testid="delete-account-button"><Trash2 size={17} className="mr-2" /> Supprimer mon compte</Button>
          </section>
        </motion.div>
      </div>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="rounded-2xl sm:max-w-[440px]" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700 dark:text-rose-400"><AlertTriangle size={20} /> Supprimer définitivement votre compte ?</DialogTitle>
            <DialogDescription>Toutes vos données (classes, élèves, passeports, signatures, comptes parents, calendrier, modèles d'e-mail) seront supprimées. Cette action est irréversible. Saisissez votre mot de passe pour confirmer.</DialogDescription>
          </DialogHeader>
          <Input type="password" placeholder="Votre mot de passe" value={delPw} onChange={(e) => setDelPw(e.target.value)} data-testid="delete-account-password" />
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setDelOpen(false)} data-testid="cancel-delete-account">Annuler</Button>
            <Button className="rounded-xl bg-rose-600 text-white hover:bg-rose-700" disabled={deleting || !delPw} onClick={deleteAccount} data-testid="confirm-delete-account">{deleting ? <Loader2 size={16} className="animate-spin" /> : "Supprimer définitivement"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TeacherShell>
  );
}
